class ScBundles extends HTMLElement {
  connectedCallback() {
    this.form = document.getElementById(this.getAttribute('form-id'));
    this.inputOwner = `sc-bundles-${Math.random().toString(36).slice(2)}`;
    this.swiperInitAttempts = 0;
    this.selectedByGroup = new Map();
    this.swipers = [];

    this._handleClick = this._handleClick.bind(this);
    this._handleKeydown = this._handleKeydown.bind(this);
    this._handleChange = this._handleChange.bind(this);
    this._handleSubmit = this._handleSubmit.bind(this);

    this.addEventListener('click', this._handleClick);
    this.addEventListener('keydown', this._handleKeydown);
    this.addEventListener('change', this._handleChange);

    if (this.form) {
      this.form.addEventListener('submit', this._handleSubmit, true);
    }

    this._initSwipers();
    this._syncSelectedCount();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._handleClick);
    this.removeEventListener('keydown', this._handleKeydown);
    this.removeEventListener('change', this._handleChange);

    if (this.form) {
      this.form.removeEventListener('submit', this._handleSubmit, true);
      this._removeBundleInputs();
    }

    this.swipers.forEach((swiper) => swiper.destroy(true, true));
    this.swipers = [];
  }

  _initSwipers() {
    if (typeof window.Swiper === 'undefined') {
      if (this.swiperInitAttempts < 120) {
        this.swiperInitAttempts += 1;
        window.requestAnimationFrame(() => {
          if (this.isConnected) {
            this._initSwipers();
          }
        });
      }

      return;
    }

    this.querySelectorAll('[data-bundle-swiper]').forEach((swiperElement) => {
      if (swiperElement.swiper) {
        this.swipers.push(swiperElement.swiper);
        return;
      }

      const group = swiperElement.closest('[data-bundle-group]');
      const prevButton = group?.querySelector('[data-bundle-group-arr-prev]');
      const nextButton = group?.querySelector('[data-bundle-group-arr-next]');
      const slideGap = this._getCssNumber(swiperElement.parentElement, '--bundle-slide-gap', 12);
      const syncSwiperState = () => this._syncSwiperState(swiper, group, prevButton, nextButton);
      const swiper = new Swiper(swiperElement, {
        slidesPerView: 'auto',
        spaceBetween: slideGap,
        watchOverflow: true,
        watchSlidesProgress: true,
        grabCursor: true,
        navigation: {
          prevEl: prevButton || null,
          nextEl: nextButton || null,
          disabledClass: 'is-disabled'
        }
      });

      swiper.on('resize', syncSwiperState);
      swiper.on('update', syncSwiperState);
      swiper.on('observerUpdate', syncSwiperState);
      swiper.on('slidesLengthChange', syncSwiperState);

      window.requestAnimationFrame(syncSwiperState);
      this.swipers.push(swiper);
    });
  }

  _syncSwiperState(swiper, group, prevButton, nextButton) {
    if (!swiper || !group) {
      return;
    }

    const isLocked = this._areAllSlidesFullyVisible(swiper);

    if (isLocked && swiper.activeIndex !== 0) {
      swiper.slideTo(0, 0, false);
    }

    group.classList.toggle('is-slider-locked', isLocked);
    swiper.el.classList.toggle('is-slider-locked', isLocked);
    swiper.allowTouchMove = !isLocked;

    [prevButton, nextButton].forEach((button) => {
      if (!button) {
        return;
      }

      button.hidden = isLocked;
      button.setAttribute('aria-hidden', isLocked ? 'true' : 'false');
      button.tabIndex = isLocked ? -1 : 0;
    });
  }

  _areAllSlidesFullyVisible(swiper) {
    const slides = Array.from(swiper.slides || []);

    if (slides.length <= 1) {
      return true;
    }

    const containerWidth = swiper.el.getBoundingClientRect().width;
    const spaceBetween = Number(swiper.params.spaceBetween) || 0;
    const slidesWidth = slides.reduce((width, slide) => width + slide.getBoundingClientRect().width, 0);
    const totalWidth = slidesWidth + spaceBetween * (slides.length - 1);

    return totalWidth <= containerWidth + 1;
  }

  _getCssNumber(element, propertyName, fallback) {
    if (!element) {
      return fallback;
    }

    const value = window.getComputedStyle(element).getPropertyValue(propertyName);
    const parsedValue = parseFloat(value);

    return Number.isNaN(parsedValue) ? fallback : parsedValue;
  }

  _handleClick(event) {
    if (event.target.closest('[data-bundle-product-link]')) {
      return;
    }

    if (event.target.closest('[data-bundle-variant-select]')) {
      return;
    }

    const slide = event.target.closest('[data-bundle-slide]');

    if (!slide || !this.contains(slide)) {
      return;
    }

    const swiperElement = slide.closest('[data-bundle-swiper]');

    if (swiperElement?.swiper && swiperElement.swiper.allowClick === false) {
      return;
    }

    this._toggleSlide(slide);
  }

  _handleKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    const slide = event.target.closest('[data-bundle-slide]');

    if (!slide || !this.contains(slide)) {
      return;
    }

    event.preventDefault();
    this._toggleSlide(slide);
  }

  _handleChange(event) {
    const variantSelect = event.target.closest('[data-bundle-variant-select]');

    if (!variantSelect || !this.contains(variantSelect)) {
      return;
    }

    const slide = variantSelect.closest('[data-bundle-slide]');

    if (!slide) {
      return;
    }

    slide.dataset.variantId = variantSelect.value;
    this._syncSlidePrice(variantSelect);

    if (slide.classList.contains('is-selected')) {
      this._syncBundleInputs();
    } else {
      this._selectSlide(slide);
    }
  }

  _handleSubmit() {
    this._syncBundleInputs();
  }

  _toggleSlide(slide) {
    if (slide.classList.contains('is-selected')) {
      this._deselectSlide(slide);
      return;
    }

    this._selectSlide(slide);
  }

  _selectSlide(slide) {
    const groupIndex = slide.dataset.bundleGroupIndex;

    if (!groupIndex || !slide.dataset.variantId) {
      return;
    }

    const selectedSlide = this.selectedByGroup.get(groupIndex);

    if (selectedSlide && selectedSlide !== slide) {
      this._setSlideSelected(selectedSlide, false);
    }

    this.selectedByGroup.set(groupIndex, slide);
    this._setSlideSelected(slide, true);
    this._syncGroupState(groupIndex);
    this._syncSelectedCount();
    this._syncBundleInputs();
  }

  _deselectSlide(slide) {
    const groupIndex = slide.dataset.bundleGroupIndex;

    if (!groupIndex) {
      return;
    }

    this.selectedByGroup.delete(groupIndex);
    this._setSlideSelected(slide, false);
    this._syncGroupState(groupIndex);
    this._syncSelectedCount();
    this._syncBundleInputs();
  }

  _setSlideSelected(slide, isSelected) {
    slide.classList.toggle('is-selected', isSelected);
    slide.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
  }

  _syncSlidePrice(variantSelect) {
    const selectedOption = variantSelect.selectedOptions?.[0];
    const productItem = variantSelect.closest('[data-bundle-product-item]');
    const priceElement = productItem?.querySelector('[data-bundle-price]');
    const comparePriceElement = productItem?.querySelector('[data-bundle-compare-price]');

    if (!selectedOption || !priceElement) {
      return;
    }

    const price = selectedOption.getAttribute('data-price') || '';
    const compareAtPrice = selectedOption.getAttribute('data-compare-at-price') || '';
    const discountLabel = selectedOption.getAttribute('data-discount-label') || '';
    const url = selectedOption.getAttribute('data-url') || '';
    const linkElement = productItem?.querySelector('[data-bundle-product-link]');
    const discountLabelElement = productItem?.querySelector('[data-bundle-discount-label]');

    priceElement.lastChild.textContent = price;
    priceElement.classList.toggle('price--highlight', compareAtPrice !== '');

    if (comparePriceElement) {
      comparePriceElement.textContent = compareAtPrice;
      comparePriceElement.hidden = compareAtPrice === '';
    }

    if (discountLabelElement) {
      discountLabelElement.innerHTML = discountLabel;
      discountLabelElement.hidden = discountLabel === '';
    }

    if (linkElement && url) {
      linkElement.href = url;
    }
  }

  _syncGroupState(groupIndex) {
    const group = this.querySelector(`[data-bundle-group][data-bundle-group-index="${groupIndex}"]`);

    if (!group) {
      return;
    }

    group.classList.toggle('has-selected-product', this.selectedByGroup.has(groupIndex));
  }

  _syncSelectedCount() {
    const selectedCountElement = this.querySelector('[data-bundles-selected]');

    if (selectedCountElement) {
      selectedCountElement.textContent = String(this.selectedByGroup.size);
    }
  }

  _syncBundleInputs() {
    if (!this.form) {
      return;
    }

    this._removeBundleInputs();

    this._getSelectedSlides().forEach((slide, index) => {
      const itemIndex = index + 1;
      const idInput = this._createHiddenInput(`items[${itemIndex}][id]`, slide.dataset.variantId);
      const quantityInput = this._createHiddenInput(`items[${itemIndex}][quantity]`, '1');

      this.form.append(idInput, quantityInput);
    });
  }

  _removeBundleInputs() {
    if (!this.form) {
      return;
    }

    this.form.querySelectorAll('[data-sc-bundle-input]').forEach((input) => {
      if (input.getAttribute('data-sc-bundle-owner') === this.inputOwner) {
        input.remove();
      }
    });
  }

  _getSelectedSlides() {
    return Array.from(this.selectedByGroup.entries())
      .sort(([groupA], [groupB]) => Number(groupA) - Number(groupB))
      .map(([, slide]) => slide)
      .filter((slide) => slide?.dataset.variantId);
  }

  _createHiddenInput(name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    input.setAttribute('data-sc-bundle-input', '');
    input.setAttribute('data-sc-bundle-owner', this.inputOwner);

    return input;
  }
}

if (!customElements.get('sc-bundles')) {
  customElements.define('sc-bundles', ScBundles);
}
